import React, { useState, useEffect, useCallback } from 'react';
import { Download, Loader2, Search, Layers } from 'lucide-react';
import { inwardAPI, vendorAPI, branchAPI } from '../../services/api';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const inr = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const qty = (n) => (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });

const LandedCostReport = () => {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [branch, setBranch] = useState('');
  const [poType, setPoType] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    vendorAPI.getAll().then(setVendors).catch(() => {});
    branchAPI.getAll().then(setBranches).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (vendorId) params.vendor_id = vendorId;
      if (branch) params.branch = branch;
      if (poType !== 'all') params.po_type = poType;
      if (search.trim()) params.search = search.trim();
      const res = await inwardAPI.getLandedCostReport(params);
      setRows(res.rows || []);
      setTotals(res.totals || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load landed cost report');
      setRows([]); setTotals({});
    } finally { setLoading(false); }
  }, [fromDate, toDate, vendorId, branch, poType, search]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vendorId, branch, poType]);

  const exportExcel = () => {
    if (!rows.length) { toast.warning('Nothing to export'); return; }
    const aoa = [[
      'Product', 'SKU', 'Category', 'Sub-category', 'Unit', 'Type', 'Total Qty', 'Receipts',
      'Purchase Rate', 'Freight', 'Loading', 'Unloading', 'Custom Charges', 'Clearance Charges', 'Other Charges',
      'Landed Rate', 'Total Landed Value', 'Last Inward',
    ]];
    rows.forEach(r => aoa.push([
      r.product_name, r.sku, r.category, r.sub_category, r.unit, r.is_import ? 'Import' : 'Domestic',
      r.total_qty, r.receipts, r.purchase_rate, r.freight, r.loading, r.unloading,
      r.custom_charges, r.clearance_charges, r.other_charges, r.landed_rate, r.total_landed_value, r.last_inward_date,
    ]));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), 'Landed Cost');
    XLSX.writeFile(wb, `landed-cost-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Excel exported');
  };

  return (
    <div data-testid="landed-cost-report">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Landed Cost Report</h2>
          <p className="text-xs text-gray-500">Weighted-average landing rate per product with charge bifurcation</p>
        </div>
        <button onClick={exportExcel} disabled={!rows.length || loading} data-testid="landed-export"
          className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-40" style={{ backgroundColor: '#24549c' }}>
          <Download className="w-3.5 h-3.5" /> Export Excel
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4 bg-white p-3 rounded-xl border border-gray-100">
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">From</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} data-testid="landed-from"
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg" />
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">To</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} data-testid="landed-to"
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg" />
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">Vendor</label>
          <select value={vendorId} onChange={e => setVendorId(e.target.value)} data-testid="landed-vendor"
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg min-w-[150px]">
            <option value="">All Vendors</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">Godown</label>
          <select value={branch} onChange={e => setBranch(e.target.value)} data-testid="landed-branch"
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg min-w-[150px]">
            <option value="">All Godowns</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">Type</label>
          <select value={poType} onChange={e => setPoType(e.target.value)} data-testid="landed-type"
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg">
            <option value="all">All</option>
            <option value="domestic">Domestic</option>
            <option value="import">Import</option>
          </select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">Search</label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
              placeholder="Product / SKU / category…" data-testid="landed-search"
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg" />
          </div>
        </div>
        <button onClick={load} data-testid="landed-apply"
          className="px-4 py-1.5 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>Apply</button>
      </div>

      {/* KPIs */}
      <div className="flex flex-wrap gap-3 mb-4">
        {[
          { l: 'Products', v: totals.product_count || 0 },
          { l: 'Total Qty', v: qty(totals.total_qty) },
          { l: 'Total Landed Value', v: inr(totals.total_landed_value) },
        ].map((k, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 px-4 py-3" data-testid={`landed-kpi-${i}`}>
            <p className="text-[10px] text-gray-500 font-semibold uppercase">{k.l}</p>
            <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{k.v}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64" data-testid="landed-loading"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#24549c' }} /></div>
      ) : !rows.length ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-sm text-gray-400" data-testid="landed-empty">
          <Layers className="w-10 h-10 mx-auto mb-2 text-gray-300" />No inward data for the selected filters
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
          <table className="w-full text-xs" data-testid="landed-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                {['Product', 'Type', 'Qty', 'Purchase Rate', 'Freight', 'Loading', 'Unloading', 'Custom', 'Clearance', 'Other', 'Landed Rate'].map(h =>
                  <th key={h} className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`landed-row-${i}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-gray-900">{r.product_name}</div>
                    <div className="text-[10px] text-gray-400">{r.sku || '—'} · {r.receipts} receipt(s)</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex px-2 py-0.5 text-[9px] font-semibold rounded-full ${r.is_import ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                      {r.is_import ? 'Import' : 'Domestic'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-gray-600">{qty(r.total_qty)} {r.unit}</td>
                  <td className="px-3 py-2.5 text-gray-900 font-medium">{inr(r.purchase_rate)}</td>
                  <td className="px-3 py-2.5 text-gray-600">{inr(r.freight)}</td>
                  <td className="px-3 py-2.5 text-gray-600">{inr(r.loading)}</td>
                  <td className="px-3 py-2.5 text-gray-600">{inr(r.unloading)}</td>
                  <td className="px-3 py-2.5 text-gray-600">{inr(r.custom_charges)}</td>
                  <td className="px-3 py-2.5 text-gray-600">{inr(r.clearance_charges)}</td>
                  <td className="px-3 py-2.5 text-gray-600">{inr(r.other_charges)}</td>
                  <td className="px-3 py-2.5 font-bold text-gray-900" style={{ color: '#24549c' }}>{inr(r.landed_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LandedCostReport;
